// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Badge,
  Card,
  CardBody,
  Input,
  Label,
  Row,
  Col,
  Collapse
} from "reactstrap";
import { Colxx } from "Components/CustomBootstrap";
import IntlMessages from "Util/IntlMessages";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { suiteApi } from "../../../../../api/suiteAdapters";
import {
  AsyncStateBoundary,
  SuitePageShell,
  SuiteListToolbar,
  MockPaginationFooter
} from "../../_suite/primitives";

const VIEWS = [
  { id: "all", label: "All open", filter: () => true },
  {
    id: "open",
    label: "Open only",
    filter: t => t.status === "open"
  },
  {
    id: "pending",
    label: "Pending",
    filter: t => t.status === "pending"
  },
  {
    id: "high",
    label: "High priority",
    filter: t => t.priority === "high"
  },
  {
    id: "sla",
    label: "SLA at risk",
    filter: t => t.slaStatus === "at_risk"
  }
];

function priorityBarClass(p) {
  if (p === "high") return "border-start border-danger border-4";
  if (p === "medium") return "border-start border-warning border-4";
  return "border-start border-secondary border-4";
}

const PAGE_SIZE = 5;

export default function TicketQueuePage() {
  const [viewId, setViewId] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState({});
  const [macroOpen, setMacroOpen] = useState(true);

  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ticketing.listTickets(),
    []
  );
  const { data: summary } = useAsyncResource(
    () => suiteApi.ticketing.getQueueSummary(),
    []
  );
  const { data: macros } = useAsyncResource(
    () => suiteApi.ticketing.listMacros(),
    []
  );

  const previewMacro = (macros || [])[0];

  const view = VIEWS.find(v => v.id === viewId) || VIEWS[0];

  const filtered = useMemo(() => {
    const list = data || [];
    const term = q.trim().toLowerCase();
    let out = list.filter(view.filter);
    if (term) {
      out = out.filter(
        t =>
          String(t.subject).toLowerCase().includes(term) ||
          String(t.id).toLowerCase().includes(term) ||
          String(t.assignee).toLowerCase().includes(term) ||
          (t.tags || []).some(tag => String(tag).toLowerCase().includes(term))
      );
    }
    return out;
  }, [data, view, q]);

  const rows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const toggleOne = (id, checked) => {
    setSelected(s => ({ ...s, [id]: checked }));
  };
  const toggleAllOnPage = checked => {
    const next = { ...selected };
    rows.forEach(r => {
      next[r.id] = checked;
    });
    setSelected(next);
  };

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.ticketing.tickets" />}
      afterBreadcrumb={
        summary && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge color="primary" pill>
              {summary.total} <IntlMessages id="suite.ticketing.summary-total" />
            </Badge>
            <Badge color="success" pill>
              {summary.open} open
            </Badge>
            <Badge color="danger" pill>
              {summary.atRisk} SLA risk
            </Badge>
            {Object.entries(summary.byQueue || {}).map(([queue, n]) => (
              <Badge key={queue} color="light" className="text-dark" pill>
                {queue}: {n}
              </Badge>
            ))}
          </div>
        )
      }
    >
      <Row>
        <Col lg="8">
          <SuiteListToolbar
            searchValue={q}
            onSearchChange={e => {
              setQ(e.target.value);
              setPage(1);
            }}
            searchPlaceholder="Search subject, ID, assignee, tags…"
          >
            <ButtonGroup size="sm" className="flex-wrap">
              {VIEWS.map(v => (
                <Button
                  key={v.id}
                  color={viewId === v.id ? "primary" : "light"}
                  onClick={() => {
                    setViewId(v.id);
                    setPage(1);
                  }}
                >
                  {v.label}
                </Button>
              ))}
            </ButtonGroup>
          </SuiteListToolbar>

          {rows.length > 0 && (
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="form-check mb-0">
                <Input
                  type="checkbox"
                  id="bulk-all"
                  checked={rows.length > 0 && rows.every(r => selected[r.id])}
                  onChange={e => toggleAllOnPage(e.target.checked)}
                  className="form-check-input"
                />
                <Label check for="bulk-all" className="small mb-0">
                  Select page
                </Label>
              </div>
              <Button
                size="sm"
                color="light"
                disabled={selectedIds.length === 0}
                onClick={() => {
                  /* demo */
                }}
              >
                <IntlMessages id="suite.ticketing.bulk-assign-demo" />
              </Button>
            </div>
          )}

          <AsyncStateBoundary
            loading={loading}
            error={error}
            empty={!loading && filtered.length === 0}
            emptyMessageId="suite.ticketing.queue-empty"
          >
            <div className="ticket-queue-list">
              {rows.map(t => (
                <Card
                  key={t.id}
                  className={`mb-3 shadow-sm ${priorityBarClass(t.priority)}`}
                >
                  <CardBody className="py-3">
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div className="d-flex gap-2 align-items-start">
                        <Input
                          type="checkbox"
                          id={`t-${t.id}`}
                          checked={!!selected[t.id]}
                          onChange={e => toggleOne(t.id, e.target.checked)}
                          className="form-check-input mt-1"
                        />
                        <div>
                          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                            <span className="text-muted small font-weight-bold">
                              {t.id}
                            </span>
                            <Badge color="light" className="text-dark">
                              {t.queue || "General"}
                            </Badge>
                            <Badge color="secondary">{t.channel}</Badge>
                            <Badge
                              color={
                                t.status === "open"
                                  ? "success"
                                  : t.status === "pending"
                                    ? "warning"
                                    : "secondary"
                              }
                            >
                              {t.status}
                            </Badge>
                            <Badge color="info">{t.priority}</Badge>
                            {t.slaStatus === "at_risk" && (
                              <Badge color="danger">
                                <IntlMessages id="suite.ticketing.sla-badge" />
                                {t.breachDueMinutes != null && (
                                  <span className="ms-1">
                                    · {t.breachDueMinutes}m
                                  </span>
                                )}
                              </Badge>
                            )}
                          </div>
                          <h5 className="mb-1">{t.subject}</h5>
                          <p className="text-muted small mb-1">
                            {t.requester} · {t.assignee} · {t.updatedAt}
                          </p>
                          {(t.tags || []).length > 0 && (
                            <div className="small">
                              {(t.tags || []).map(tag => (
                                <Badge
                                  key={tag}
                                  color="light"
                                  className="text-muted me-1"
                                  pill
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-end">
                        <Button
                          tag={Link}
                          color="primary"
                          size="sm"
                          to={t.id}
                          relative="path"
                        >
                          <IntlMessages id="suite.action.open" />
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
            <MockPaginationFooter
              page={page}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPage}
            />
          </AsyncStateBoundary>
          <Colxx xxs="12" className="mt-2">
            <p className="text-muted small mb-0">
              Bulk assign is a demo button — wire to your tickets bulk API.
            </p>
          </Colxx>
        </Col>
        <Col lg="4">
          <Card className="border-0 shadow-sm bg-light">
            <CardBody>
              <Button
                color="link"
                className="p-0 mb-2 text-decoration-none"
                onClick={() => setMacroOpen(!macroOpen)}
              >
                <strong>
                  <IntlMessages id="suite.ticketing.macro-preview-panel" />
                </strong>
                <span className="ms-1 small text-muted">
                  ({macroOpen ? "hide" : "show"})
                </span>
              </Button>
              <Collapse isOpen={macroOpen}>
                {previewMacro ? (
                  <>
                    <Badge color="dark" className="me-2 font-monospace">
                      {previewMacro.shortcut}
                    </Badge>
                    <span className="font-weight-bold">{previewMacro.title}</span>
                    <p className="small text-muted mt-2 mb-2">
                      {previewMacro.uses30d != null && (
                        <>
                          <IntlMessages id="suite.ticketing.macro-uses" />:{" "}
                          {previewMacro.uses30d}
                        </>
                      )}
                    </p>
                    <pre className="bg-white rounded p-2 small text-wrap border mb-2">
                      {previewMacro.body}
                    </pre>
                    <Button tag={Link} to="../macros" relative="path" size="sm" color="primary">
                      <IntlMessages id="suite.ticketing.all-macros" />
                    </Button>
                  </>
                ) : (
                  <p className="small text-muted mb-0">No macros loaded.</p>
                )}
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </SuitePageShell>
  );
}
